<!--
 * @Author: Tahiti
 * @Date: 2025-10-30
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-10-30 19:05:17
 * @Description: AI Video Models - Free Video Generation Tools | 微鲤AI
 * @FilePath: /weryai-web/pages/video/index.vue
-->
<template>
    <div class="video-models-page text-white w-full h-full">
        <div class="page-header mb-10 text-center">
            <h1 class="text-3xl text-center md:text-4xl font-bold mb-4 text-white">
                {{ t('homeView.videoModels') }}
            </h1>
            <p class="text-text-adad text-base text-center md:text-lg">
                {{ t('homeView.videoModelsDesc') }}
            </p>
        </div>

        <div class="w-full mb-5">
            <div class="grid px-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
                <div v-for="item in modelList" :key="item.path"
                    class="model-card flex cursor-pointer items-center justify-between h-auto min-h-[80px] p-4 gap-4 rounded-xl bg-[#151718] border border-transparent transition-all duration-300 hover:border-white/30 hover:shadow-lg hover:shadow-text-active/10"
                    @click="handleModelClick(item)">
                    <div class="flex gap-3 items-center flex-1 min-w-0">
                        <img :src="item.thumbnail" :alt="`${item.title} AI model thumbnail`"
                            class="w-12 h-12 rounded-lg flex-shrink-0 object-cover" width="48" height="48"
                            loading="lazy" />
                        <div class="flex flex-col items-start justify-center gap-1 flex-1 min-w-0">
                            <h2 class="text-white text-sm font-semibold flex items-center gap-2 w-full">
                                <span class="truncate inline-block">{{
                                    item.title
                                    }}</span>
                                <span v-if="item.is_new"
                                    class="px-2 py-[2px] text-[10px] text-white bg-[#00AC78] rounded-[10px] flex-shrink-0">{{
                                    t('homeView.new') }}</span>
                                <span v-if="item.is_audio"
                                    class="px-2 py-[2px] text-[10px] text-white bg-[#B91C1C] rounded-[10px] flex-shrink-0">{{
                                    t('homeView.audio') }}</span>
                            </h2>
                            <p class="text-text-adad text-xs line-clamp-2 leading-relaxed">
                                {{ item.desc }}
                            </p>
                        </div>
                    </div>
                    <button
                        class="try-button px-4 py-2 text-white text-sm font-medium rounded-lg transition-all duration-300 flex-shrink-0"
                        :aria-label="`${t('homeView.try')} ${item.title}`" @click.stop="handleModelClick(item)">
                        {{ t('homeView.try') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { definePageMeta, useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { useToolsHooks } from '~/utils/useHooks'

definePageMeta({
    layout: 'website',
})

const { t } = useI18n()
const { videoModelsEnums } = useToolsHooks()

// 设置多语言 SEO Meta 标签
useMultilingualSeo({
    pageType: 'videoModels',
})

// BreadcrumbList + ItemList 结构化数据
const requestUrlHref = useRequestURL().href

useHead({
    script: [
        // BreadcrumbList Schema
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: '微鲤AI',
                            item: 'https://www.weryai.com.cn',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('homeView.videoModels'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
        // ItemList Schema for AI Video Models
        {
            key: 'itemlist-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: t('homeView.videoModels'),
                    description: t('seo.videoModels.description'),
                    numberOfItems: videoModelsEnums.value?.length || 0,
                    itemListElement:
                        videoModelsEnums.value?.map((item: any, index: number) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            name: item.title,
                            description: item.desc,
                            url: `https://www.weryai.com${item.path}`,
                        })) || [],
                })
            ),
        },
    ],
})

// 获取视频模型列表
const modelList = computed(() => {
    return videoModelsEnums.value
})

// 点击模型卡片
const handleModelClick = (item: any) => {
    analyticsEvent('video_model_click', {
        model_path: item.path,
        model_title: item.title,
    })
    navigateTo(item.path)
}
</script>

<style scoped lang="less">
.video-models-page {
    padding: 20px 0;
}

.page-header {
    text-align: center;
    padding: 20px 0;
}

.models-section {
    width: 100%;
}

.model-card {
    transition: all 0.3s ease;

    h2 {
        margin: 0;
        font-size: inherit;
    }

    &:hover {
        transform: translateY(-2px);

        .try-button {
            opacity: 1;
            background: var(--el-menu-active-bg);
            border-color: var(--weryai-text-active);
            color: var(--weryai-text-active);
        }
    }
}

.try-button {
    border-radius: 100px;
    border: 1px solid var(--weryai-text-active);
    background: transparent;
    color: var(--weryai-text-active);
    opacity: 0.6;

    &:hover {
        opacity: 1;
        background: var(--el-menu-active-bg);
    }
}
</style>
